import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "../Input";
import { Select } from "../Select";
import { Combobox } from "../Combobox";
import { useLocalizedStrings } from "../LocalizationProvider";
export const isValidDate = (dateString) => {
    if (!dateString?.trim())
        return false;
    const date = new Date(dateString);
    return !isNaN(date.getTime());
};
export const isValidNumber = (numberString) => {
    if (!numberString?.trim())
        return false;
    const number = Number(numberString);
    return !isNaN(number) && isFinite(number);
};
export const normalizeNumericString = (numberString) => {
    if (!isValidNumber(numberString)) {
        return numberString;
    }
    const number = Number(numberString);
    return number.toString();
};
export const FilterValueInput = ({ fieldType, value, onChange, onDebouncedChange, onValidChange, placeholder, className, debounceDelay = 300, fieldKey, onFetchSuggestions, allowFreeText = true }) => {
    const L = useLocalizedStrings();
    const [localValue, setLocalValue] = useState(value);
    const [options, setOptions] = useState([]);
    const [valueLabel, setValueLabel] = useState();
    const debounceTimeoutRef = useRef(null);
    const fetchTimeoutRef = useRef(null);
    useEffect(() => {
        setLocalValue(value);
    }, [value]);
    const handleQueryChange = useCallback(async (query) => {
        if (!fieldKey || !onFetchSuggestions)
            return;
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }
        fetchTimeoutRef.current = setTimeout(async () => {
            const suggestions = await onFetchSuggestions(fieldKey, query);
            if (suggestions.length > 0 && typeof suggestions[0] === 'object' && 'value' in suggestions[0]) {
                setOptions(suggestions);
            }
            else {
                setOptions(suggestions.map(s => ({
                    value: s,
                    label: s.length > 30 ? `${s.slice(0, 30)}...` : s
                })));
            }
        }, 200);
    }, [fieldKey, onFetchSuggestions]);
    const handleImmediateChange = useCallback((newValue) => {
        setLocalValue(newValue);
        onChange(newValue);
    }, [onChange]);
    const handleDebouncedChange = useCallback((newValue) => {
        setLocalValue(newValue);
        onChange(newValue);
        if (onDebouncedChange) {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            debounceTimeoutRef.current = setTimeout(() => {
                onDebouncedChange(newValue);
            }, debounceDelay);
        }
    }, [onChange, onDebouncedChange, debounceDelay]);
    useEffect(() => {
        if (fieldKey && onFetchSuggestions) {
            (async () => {
                try {
                    const suggestions = await onFetchSuggestions(fieldKey, '');
                    if (suggestions.length > 0 && typeof suggestions[0] === 'object' && 'value' in suggestions[0]) {
                        setOptions(suggestions);
                    }
                    else {
                        setOptions(suggestions.map(s => ({
                            value: s,
                            label: s.length > 30 ? `${s.slice(0, 30)}...` : s
                        })));
                    }
                    const listed = suggestions.find(s => (typeof s === 'string' ? s : s.value) === value);
                    if (listed && typeof listed !== 'string') {
                        setValueLabel(listed);
                    }
                    else if (fieldType === 'user' && value && !listed) {
                        const matches = await onFetchSuggestions(fieldKey, value);
                        const match = matches.find((m) => typeof m !== 'string' && m.value === value);
                        if (match)
                            setValueLabel(match);
                    }
                }
                catch (err) {
                    console.error('Failed to fetch initial suggestions:', err);
                }
            })();
        }
    }, [fieldKey]);
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, []);
    switch (fieldType) {
        case 'number':
            return (_jsx(Input, { type: "number", thousandSeparator: ",", allowNegative: true, value: localValue, onChange: (e) => {
                    const inputValue = e.target.value;
                    setLocalValue(inputValue);
                    onChange(inputValue);
                    if (isValidNumber(inputValue)) {
                        onValidChange?.(inputValue);
                    }
                }, onBlur: (e) => {
                    const inputValue = e.target.value;
                    if (isValidNumber(inputValue)) {
                        const normalizedValue = normalizeNumericString(inputValue);
                        setLocalValue(normalizedValue);
                        onChange(normalizedValue);
                        onValidChange?.(normalizedValue);
                    }
                }, placeholder: placeholder || L.component_library.filter_panel.enter_a_value, label: L.component_library.filter_panel.filter_value, showLabel: false, className: className, wrapperClassName: "sm:max-w-none" }));
        case 'date':
            const isDateValid = localValue === '' || isValidDate(localValue);
            return (_jsx(Input, { type: "date", dateFormat: "MM/DD/YYYY", value: localValue, onChange: (e) => {
                    const newValue = e.target.value;
                    setLocalValue(newValue);
                    onChange(newValue);
                    if (newValue !== '' && isValidDate(newValue)) {
                        onValidChange?.(newValue);
                    }
                    else if (newValue === '') {
                        onValidChange?.(newValue);
                    }
                }, placeholder: "MM/DD/YYYY", label: L.component_library.filter_panel.filter_value, showLabel: false, className: className, wrapperClassName: "sm:max-w-none", error: !isDateValid ? {
                    hasError: !isDateValid,
                    errorMessage: L.component_library.filter_panel.enter_a_valid_date
                } : undefined }));
        case 'boolean':
            const booleanOptions = [
                { value: 'true', label: L.component_library.filter_panel.true },
                { value: 'false', label: L.component_library.filter_panel.false }
            ];
            const selectedBooleanOption = booleanOptions.find(opt => opt.value === localValue);
            return (_jsx(Select, { options: booleanOptions, value: selectedBooleanOption, onChange: (option) => handleImmediateChange(option.value), description: "", label: L.component_library.filter_panel.filter_value, labelLocation: "above", hideLabel: true, portal: true, className: className }));
        case 'text':
        default:
            if (fieldKey && onFetchSuggestions) {
                const selectedOption = options.find(opt => opt.value === localValue);
                let fallbackLabel = localValue;
                if (!selectedOption && valueLabel?.value === localValue) {
                    fallbackLabel = valueLabel.label;
                }
                else if (!selectedOption && localValue) {
                    try {
                        const parsed = JSON.parse(localValue);
                        if (parsed.name) {
                            fallbackLabel = parsed.name;
                        }
                    }
                    catch {
                        fallbackLabel = localValue;
                    }
                }
                const comboboxValue = selectedOption || (localValue ? { value: localValue, label: fallbackLabel } : { value: '', label: '' });
                return (_jsx(Combobox, { label: L.component_library.filter_panel.filter_value, showLabel: false, value: comboboxValue, options: options, multiple: false, by: "value", allowFreeText: allowFreeText, portal: true, onQueryChange: handleQueryChange, onChange: (selected) => {
                        setValueLabel(selected);
                        handleDebouncedChange(selected.value);
                    }, className: className }));
            }
            return (_jsx(Input, { type: "text", value: localValue, onChange: (e) => {
                    handleDebouncedChange(e.target.value);
                }, placeholder: placeholder, label: L.component_library.filter_panel.filter_value, showLabel: false, className: className, wrapperClassName: "sm:max-w-none" }));
    }
};
